<script lang="ts" setup>
  import emailSpellChecker from '@zootools/email-spell-checker'
  import ImageSplashSignUp from '~/assets/images/login/splash-sign-up.png'
  import GInputField from '~/components/form/GInputField/GInputField.vue'
  import { madBundayTermsAndConditionsLink } from '~/constants/links'
  import { z } from 'zod'
  import { EMAIL_INVALID_ERROR, PASSWORD_LENGTH_ERROR_MESSAGE } from '~/constants/errorMessage'
  import { useNavigationStore } from '~/store'

  const LazyMadBundayRegistrationForm = defineAsyncComponent(
    () => import('~/components/promotion/GMadBundayRegistrationForm/GMadBundayRegistrationForm.vue')
  )

  const props = withDefaults(
    defineProps<{
      modalId?: string
      hideBackButton?: boolean
      isView?: boolean
    }>(),
    {
      modalId: undefined,
      hideBackButton: false,
      isView: false
    }
  )

  const route = useRoute()
  const vueRouter = useRouter()

  const { isMobileMode, isWebMode } = useAppMode()
  const { isConnected, clearNetworkListener } = useNetworkStatus()
  const { navigateToUserDetailsForm, loginDetails, shouldShowMadBundayPromoFromCarousel, queries } =
    useJoinRelishModal()
  const { showSignInModal } = useSignInModal()
  const { setChosenLeague, setChosenTeam, chosenLeague, chosenTeam } = useMadBundayForm()

  const contentEl = useKeyboardOpenAndScroll()
  const navigationStore = useNavigationStore()
  const { hasImageLoaded, onImageLoad, onImageError } = useImageLoader()

  const isLoading = ref(true)
  const shouldShowMadBundayPromo = computed(() => {
    return route.query.r === 'mad-bunday' || shouldShowMadBundayPromoFromCarousel.value
  })

  const emailInputElement = ref<InstanceType<typeof GInputField> | null>(null)
  const errorMessage = ref('')
  const isGreenNotification = ref(false)

  const validationSchema = toTypedSchema(
    z.object({
      email: z
        .string()
        .min(6, { message: 'Minimum 6 characters' })
        .max(100, { message: 'Max 100 characters' })
        .email({ message: EMAIL_INVALID_ERROR }),
      password: z.string().min(8, { message: PASSWORD_LENGTH_ERROR_MESSAGE })
    })
  )

  const { handleSubmit, meta, values, isSubmitting } = useForm({
    validationSchema,
    initialValues: {
      email: '',
      password: ''
    }
  })

  const isSubmitDisabled = computed(() => {
    return !meta.value.valid || !isConnected.value || isSubmitting.value
  })

  async function dismissModal() {
    await modalController.dismiss(undefined, undefined, props.modalId)
    await clearNetworkListener()
  }

  const onSubmit = handleSubmit(async (values) => {
    isGreenNotification.value = false
    errorMessage.value = ''

    try {
      const emailExists = await lookupEmail({ emailAddress: values.email })

      if (emailExists) {
        errorMessage.value = 'This email is not available. Choose a different address or sign in.'
        throw new Error('Email already exists')
      }

      loginDetails.value = values

      if (props.isView) {
        await vueRouter.push({ name: 'sign-up-details', query: { r: route.query.r } })
        await clearNetworkListener()
        gaEventJoinRelishSecondPageViewed()
      } else {
        await navigateToUserDetailsForm()
      }
    } catch (e: any) {
      console.error(e)
    } finally {
      isSubmitting.value = false
    }
  })

  async function onSignInLinkClicked() {
    if (props.isView) {
      await clearNetworkListener()
      await vueRouter.replace({ name: 'sign-in', query: { ...queries.value } })
    } else {
      await dismissModal()
      await showSignInModal({ queries: queries.value })
    }
  }

  function onEmailInputBlur() {
    if (!values.email) {
      return
    }

    const suggestedEmailChecker = emailSpellChecker.run({ email: values.email })

    if (suggestedEmailChecker) {
      isGreenNotification.value = true
      errorMessage.value = `Did you mean ${suggestedEmailChecker.domain}`
    } else {
      isGreenNotification.value = false
      errorMessage.value = ''
    }
  }

  function handleBackNavigation() {
    gaEventSignUpFirstPageModalNavigateBack()

    if (props.isView) {
      const routeFrom = vueRouter.options.history.state.back as string
      if (routeFrom) {
        vueRouter.back()
      }
    } else {
      dismissModal()
    }
  }

  watchEffect(() => {
    if (!isConnected.value) {
      errorMessage.value = 'No Internet Connection'
    } else {
      errorMessage.value = ''
    }
  })

  onMounted(async () => {
    gaEventJoinRelishFirstPageViewed()

    const focusOnInputFieldMs = isWebMode ? 1000 : 500

    setTimeout(() => {
      if (shouldShowMadBundayPromo.value) {
        return
      }

      if (!emailInputElement.value) {
        return
      }

      emailInputElement.value.$el.setFocus()
    }, focusOnInputFieldMs)

    try {
      navigationStore.signUpExperience = await fetchSignUpExperience(
        navigationStore.experienceTag,
        navigationStore.referralCode
      )
    } catch (error) {
      console.warn('Error fetching sign-up experience:', error)
    } finally {
      isLoading.value = false
    }
  })
</script>

<template>
  <GPageHeader
    :back-button-func="handleBackNavigation"
    :hide-back-button="hideBackButton"
    title="Sign up"
    compact
    disable-box-shadow
  />

  <ion-content ref="contentEl" class="ion-padding-horizontal">
    <div class="hero-section">
      <GSkeletonImage v-if="isLoading" width="100%" aspect-ratio="4/3" border-radius="32px" class="hero-image" />
      <template v-else-if="shouldShowMadBundayPromo">
        <LazyMadBundayRegistrationForm
          :chosen-league="chosenLeague"
          :chosen-team="chosenTeam"
          class="pt-3 pb-2"
          @on-league-selected="setChosenLeague"
          @on-team-selected="setChosenTeam"
        />
        <div class="mad-bunday-conditions-text">
          Your team is for life (it can't be changed later).
          <br />
          By choosing a team you agree to the
          <a :href="madBundayTermsAndConditionsLink" target="_blank">Mad Bunday Promotion T&Cs</a>
        </div>
      </template>
      <template v-else-if="navigationStore.signUpExperience?.headerImageUrl">
        <div class="loading-image">
          <GSkeletonImage
            v-if="!hasImageLoaded"
            aspect-ratio="4/3"
            border-radius="32px"
            class="image-background-skeleton"
          />

          <div class="image-container">
            <img
              alt="Sign up"
              :src="navigationStore.signUpExperience.headerImageUrl ?? ImageSplashSignUp"
              class="hero-image"
              @error="onImageError"
              @load="onImageLoad"
            />
          </div>
        </div>
        <span class="body flex justify-center pt-2">Let's start with the basics</span>
      </template>
      <template v-else>
        <img :src="ImageSplashSignUp" alt="Sign up" class="hero-image" />
        <span class="body flex justify-center pt-2">Let's start with the basics</span>
      </template>
    </div>

    <div v-if="errorMessage" class="notification-wrapper">
      <GNotification :is-error="!isGreenNotification" :message="errorMessage" />
    </div>

    <form id="sign-up-form" @submit.prevent.stop="onSubmit">
      <GInputField
        ref="emailInputElement"
        :max-length="100"
        label="Your email"
        name="email"
        type="email"
        @blur="onEmailInputBlur"
      />
      <GInputField label="Password" name="password" type="password" />
    </form>

    <div v-if="isWebMode">
      <GButton
        :disabled="isSubmitDisabled"
        :should-show-loading-animation="isSubmitting"
        animation-color="light"
        block
        label="Continue"
        @click="onSubmit"
      />
      <div class="sign-up-link">
        <span class="body">Already have an account?</span>
        <a @click="onSignInLinkClicked" @keydown="onSignInLinkClicked">Sign In</a>
      </div>
    </div>
  </ion-content>

  <ion-footer v-if="isMobileMode">
    <ion-toolbar>
      <GButton
        :disabled="isSubmitDisabled"
        :should-show-loading-animation="isSubmitting"
        animation-color="light"
        block
        label="Continue"
        @click="onSubmit"
      />
      <div class="sign-up-link">
        <span class="body">Already have an account?</span>
        <a @click="onSignInLinkClicked" @keydown="onSignInLinkClicked">Sign In</a>
      </div>
    </ion-toolbar>
  </ion-footer>
</template>

<style lang="scss" scoped>
  .hero-section {
    padding-bottom: 8px;
  }

  .loading-image {
    position: relative;
    aspect-ratio: 4/3;

    .image-background-skeleton {
      position: absolute;
      top: 0;
      left: 0;
      z-index: 0;
    }

    .image-container {
      position: relative;
      aspect-ratio: 4/3;
    }
  }

  .hero-image {
    width: 100%;
    border-radius: 32px;
  }

  .notification-wrapper {
    padding-bottom: 12px;
  }

  #sign-up-form {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 32px;
  }

  a {
    text-decoration: none;
  }

  .sign-up-link {
    display: flex;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding-top: 8px;
  }

  ion-footer ion-toolbar {
    --padding-top: 0px;
    --padding-start: 8px;
    --padding-bottom: 30px;
    --padding-end: 8px;
  }

  .mad-bunday-conditions-text {
    a {
      font-size: 14px !important;
      text-decoration: underline !important;
    }

    @include typography-body-small;
    color: var(--color-text-muted);
    margin-bottom: 15px;
  }
</style>
